import { ErrorHandler, Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

type SentrySdk = typeof import('@sentry/angular');

@Injectable({ providedIn: 'root' })
export class SentryService {
  private initialized = false;
  private sdk?: SentrySdk;
  private sdkPromise?: Promise<SentrySdk | null>;

  initialize(): void {
    if (!this.isEnabled() || this.initialized) return;

    this.scheduleIdle(() => {
      void this.loadSdk();
    });
  }

  identify(user: { id: string | number; email?: string }): void {
    if (!this.isEnabled()) return;

    void this.loadSdk().then((Sentry) => {
      Sentry?.setUser({
        id: String(user.id),
        email: user.email,
      });
    });
  }

  clearUser(): void {
    if (!this.isEnabled()) return;

    void this.loadSdk().then((Sentry) => {
      Sentry?.setUser(null);
    });
  }

  captureException(error: unknown, context?: Record<string, unknown>): void {
    if (!this.isEnabled()) {
      console.error('[Sentry disabled]', error, context);
      return;
    }

    void this.loadSdk().then((Sentry) => {
      if (!Sentry) {
        console.error('[Sentry not initialized]', error, context);
        return;
      }

      Sentry.captureException(error, {
        extra: context,
      });
    });
  }

  addBreadcrumb(category: string, message: string, data?: Record<string, unknown>): void {
    if (!this.isEnabled()) return;

    void this.loadSdk().then((Sentry) => {
      Sentry?.addBreadcrumb({
        category,
        message,
        data,
        level: 'info',
      });
    });
  }

  private isEnabled(): boolean {
    const dsn = (environment as any).SENTRY_DSN;
    return !!dsn && dsn !== 'OFF';
  }

  private loadSdk(): Promise<SentrySdk | null> {
    if (this.sdk) return Promise.resolve(this.sdk);
    if (this.sdkPromise) return this.sdkPromise;

    const dsn = (environment as any).SENTRY_DSN;
    if (!dsn || dsn === 'OFF') return Promise.resolve(null);

    this.sdkPromise = import('@sentry/angular')
      .then((Sentry) => {
        if (!this.initialized) {
          Sentry.init({
            dsn,
            environment: environment.ENVIRONMENT,
            release: (environment as any).APP_VERSION || 'unknown',
            beforeSend(event) {
              if (event.request) {
                delete (event.request as any).cookies;
                if (event.request.headers) {
                  delete (event.request.headers as any)['Authorization'];
                  delete (event.request.headers as any)['authorization'];
                }
              }
              if (event.extra) {
                for (const key of ['password', 'token', 'refresh_token', 'access_token', 'biometry_assertion']) {
                  delete (event.extra as any)[key];
                }
              }
              return event;
            },
          });

          this.initialized = true;
        }

        this.sdk = Sentry;
        return Sentry;
      })
      .catch((error) => {
        this.sdkPromise = undefined;
        console.warn('[Sentry load failed]', error);
        return null;
      });

    return this.sdkPromise;
  }

  private scheduleIdle(task: () => void): void {
    if (typeof window === 'undefined') return;

    const win = window as Window & {
      requestIdleCallback?: (
        callback: () => void,
        options?: { timeout: number },
      ) => number;
    };

    if (typeof win.requestIdleCallback === 'function') {
      win.requestIdleCallback(task, { timeout: 8000 });
      return;
    }

    window.setTimeout(task, 2500);
  }
}

@Injectable()
export class LazySentryErrorHandler implements ErrorHandler {
  constructor(private sentry: SentryService) {}

  handleError(error: unknown): void {
    this.sentry.captureException(error);
    console.error(error);
  }
}
